// src/salesAssessment/SalesAssessmentComparisonDashboard.js
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Replay as ReprocessIcon,
  Download as DownloadIcon,
  Description as DescriptionIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import api from '../config/api';
import useStore from '../config/store';
import { useTranslation } from 'react-i18next';
import SalesAssessmentComparisonResults from './SalesAssessmentComparisonResults';

const formatDate = (timestamp) => {
  if (!timestamp) return '-';
  const date = timestamp._seconds
    ? new Date(timestamp._seconds * 1000)
    : new Date(timestamp);
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusColor = (status) => {
  const map = {
    completado: 'success',
    procesando: 'info',
    pendiente: 'warning',
    error: 'error',
  };
  return map[status] || 'default';
};

export default function SalesAssessmentComparisonDashboard() {
  const { authToken, project, user } = useStore.getState();
  const { t } = useTranslation();

  const getStatusLabel = (status) => {
    const labels = {
      completado: t('salesAssessment.comparison.statusCompleted'),
      procesando: t('salesAssessment.comparison.statusProcessing'),
      pendiente: t('salesAssessment.comparison.statusPending'),
      error: t('salesAssessment.comparison.statusError'),
    };
    return labels[status] || status;
  };

  const [comparisons, setComparisons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, comparison: null });
  const [deleting, setDeleting] = useState(false);
  const [reprocessing, setReprocessing] = useState({});

  // Modal para ver resultados — igual que DiscDashboardComparison
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedComparison, setSelectedComparison] = useState(null);

  const handleViewComparison = (comparison) => {
    setSelectedComparison(comparison);
    setModalOpen(true);
  };

  useEffect(() => {
    if (project && authToken) {
      fetchComparisons();
    }
  }, [project, authToken]);

  const fetchComparisons = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get(`/sales-assessment-comparisons/by-project`, {
        params: { projectId: project },
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (data.response && data.response.status_response === 'OK') {
        setComparisons(data.response.data || []);
      } else {
        setComparisons([]);
      }
    } catch (err) {
      console.error('Error al cargar comparaciones:', err);
      setError(t('salesAssessment.comparison.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleReprocess = async (id) => {
    try {
      setReprocessing(prev => ({ ...prev, [id]: true }));
      await api.post(
        `/sales-assessment-comparisons/${id}/reprocess`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      await fetchComparisons();
    } catch (err) {
      console.error('Error al reprocesar:', err);
    } finally {
      setReprocessing(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleOpenDeleteDialog = (comparison) => {
    setDeleteDialog({ open: true, comparison });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({ open: false, comparison: null });
  };

  const handleConfirmDelete = async () => {
    const { comparison } = deleteDialog;
    if (!comparison) return;
    try {
      setDeleting(true);
      await api.delete(
        `/sales-assessment-comparisons/${comparison.id}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      handleCloseDeleteDialog();
      await fetchComparisons();
    } catch (err) {
      console.error('Error al eliminar:', err);
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!selectedComparison?.reportUrl) return;
    try {
      const response = await fetch(selectedComparison.reportUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `sales-assessment-comparison-${selectedComparison.id || 'reporte'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error al descargar PDF:', error);
      window.open(selectedComparison.reportUrl, '_blank');
    }
  };

  const handleDownloadWord = async () => {
    if (!selectedComparison?.wordUrl) return;
    try {
      const response = await fetch(selectedComparison.wordUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `sales-assessment-comparison-${selectedComparison.id || 'reporte'}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error al descargar Word:', error);
      window.open(selectedComparison.wordUrl, '_blank');
    }
  };

  const canDelete = () => {
    return ['super-admin', 'admin', 'admin-grafton'].includes(user?.rol);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchComparisons}
          size="small"
        >
          {t('salesAssessment.dashboard.refresh')}
        </Button>
      </Box>

      {comparisons.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            {t('salesAssessment.comparison.noComparisons')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('salesAssessment.comparison.noComparisonsDescription')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('salesAssessment.comparison.position')}</TableCell>
                <TableCell>{t('salesAssessment.comparison.persons')}</TableCell>
                <TableCell>{t('salesAssessment.comparison.status')}</TableCell>
                <TableCell>{t('createdAt')}</TableCell>
                <TableCell>{t('salesAssessment.comparison.createdBy')}</TableCell>
                <TableCell align="right">{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {comparisons.map(comparison => (
                <TableRow key={comparison.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {comparison.position_name || t('salesAssessment.comparison.noPosition')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {comparison.tests?.length || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(comparison.status)}
                      color={getStatusColor(comparison.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(comparison.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {comparison.userEmail || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                      {/* Ver resultados */}
                      <Tooltip title={t('view')}>
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={comparison.status !== 'completado'}
                            onClick={() => handleViewComparison(comparison)}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      {/* Reprocesar */}
                      <Tooltip title={t('reprocess')}>
                        <span>
                          <IconButton
                            size="small"
                            color="secondary"
                            disabled={
                              comparison.status === 'procesando' ||
                              !!reprocessing[comparison.id]
                            }
                            onClick={() => handleReprocess(comparison.id)}
                          >
                            {reprocessing[comparison.id] ? (
                              <CircularProgress size={16} />
                            ) : (
                              <ReprocessIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>

                      {/* Eliminar */}
                      {canDelete() && (
                        <Tooltip title={t('delete')}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDeleteDialog(comparison)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Modal para ver resultados — igual que DiscDashboardComparison */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            width: '95vw',
            maxWidth: '95vw',
            margin: 2,
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6">
            {t('salesAssessment.comparison.resultsTitle')}
            {selectedComparison?.position_name && ` — ${selectedComparison.position_name}`}
          </Typography>
          <IconButton onClick={() => setModalOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, height: '100%', overflow: 'auto' }}>
          {selectedComparison && (
            <SalesAssessmentComparisonResults
              comparisonId={selectedComparison.id}
              onBack={() => setModalOpen(false)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<DownloadIcon />}
            variant="outlined"
            onClick={handleDownloadPDF}
            disabled={!selectedComparison?.reportUrl}
          >
            {t('salesAssessment.reportModal.downloadPDF')}
          </Button>
          <Button
            startIcon={<DescriptionIcon />}
            variant="outlined"
            onClick={handleDownloadWord}
            disabled={!selectedComparison?.wordUrl}
          >
            {t('salesAssessment.reportModal.downloadWord')}
          </Button>
          <Button onClick={() => setModalOpen(false)}>
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog confirmar eliminar */}
      <Dialog open={deleteDialog.open} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('salesAssessment.comparison.deleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('salesAssessment.comparison.deleteConfirm')}
          </Typography>
          {deleteDialog.comparison && (
            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>{t('salesAssessment.comparison.position')}:</strong>{' '}
                {deleteDialog.comparison.position_name || t('salesAssessment.comparison.noPosition')}
              </Typography>
              <Typography variant="body2">
                <strong>{t('salesAssessment.comparison.persons')}:</strong>{' '}
                {deleteDialog.comparison.tests?.length || 0}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            {t('noWayBack')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleting}>
            {t('cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} color="inherit" /> : t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}